// A bounded observe -> propose -> apply -> observe loop in the user's app session.
const invoiceLiveControlApi = (() => {
  const createSession = (deps: any) => {
    let state: any = { messages: [], busy: false, error: '', form: null, saved: false, progress: '', steps: 0 }
    let generation = 0, disposed = false, saving = false, stopRequested = false, activeRequest: AbortController | null = null
    let approvalResolve: ((accepted: boolean) => void) | null = null
    const decideApproval = (id: string, accepted: boolean, event: any) => {
      if (event?.isTrusted !== true || state.approval?.id !== id || !deps.isCurrent()) return false
      if (state.approval.route !== deps.getRoute()) { stop(); return false }
      const resolve = approvalResolve; approvalResolve = null
      publish({ approval: null }); resolve?.(accepted); return true
    }
    const cancelApproval = () => { const resolve = approvalResolve; approvalResolve = null; publish({ approval: null }); resolve?.(false) }
    let objective = '', observations: any[] = [], appliedPageSteps = new Set<string>()
    const listeners = new Set<() => void>()
    const publish = (patch: any) => { state = { ...state, ...patch }; listeners.forEach(fn => fn()) }
    const invoiceList = deps.invoiceList
    const unsubscribeList = invoiceList?.subscribe(() => publish({ invoiceList: invoiceList.getSnapshot() }))
    const registry = deps.registry
    const unsubscribeRegistry = registry?.subscribe(() => publish({ registry: registry.getSnapshot() }))
    const appSurface = deps.appSurface
    const pageFlow = deps.pageFlow
    const unsubscribePageFlow = pageFlow?.subscribe(() => publish({ pageFlow: pageFlow.getSnapshot() }))
    const active = (token: number) => !disposed && token === generation && deps.isCurrent()
    const editor = (): any => deps.getRoute() === 'faktura' ? window.PlummeInvoiceControl.currentEditor(deps.businessId) : null
    const message = (content: string) => publish({ messages: [...state.messages, { role: 'assistant', content }].slice(-30) })
    const clearStreamingMessage = () => {
      if (state.messages.at(-1)?.streaming) publish({ messages: state.messages.slice(0, -1) })
    }
    const streamMessage = (content: string) => {
      const messages = state.messages.at(-1)?.streaming
        ? [...state.messages.slice(0, -1), { role: 'assistant', content, streaming: true }]
        : [...state.messages, { role: 'assistant', content, streaming: true }]
      publish({ messages: messages.slice(-30) })
    }
    const settleStreamingMessage = (content: string) => {
      if (!state.messages.at(-1)?.streaming) { message(content); return }
      publish({ messages: [...state.messages.slice(0, -1), { role: 'assistant', content }].slice(-30) })
    }
    const pause = () => new Promise<void>(resolve => setTimeout(resolve, 60))
    let genericExecution = false, genericToken = 0
    const mutationApproval = globalThis.PlummeAssistantMutationApproval
    mutationApproval?.wrapBackend((globalThis as any).PlummeBackend)
    mutationApproval?.bind({ businessId: deps.businessId,
      enabled: () => genericExecution && (state.busy || saving),
      isCurrent: () => !disposed && deps.isCurrent() && genericToken === generation && !stopRequested,
      confirm: (approval: any) => new Promise<boolean>(resolve => {
        if (approvalResolve) { resolve(false); return }
        approvalResolve = resolve
        publish({ approval: { ...approval, route: deps.getRoute(), id: crypto.randomUUID() }, progress: 'live.approval' })
      }),
    })
    const stop = () => {
      cancelApproval()
      if (saving) { stopRequested = true; return }
      activeRequest?.abort(); activeRequest = null; generation++; clearStreamingMessage(); publish({ busy: false, form: null, progress: '', error: '' })
    }
    const reset = () => {
      if (saving || state.busy || disposed) return false
      generation++; objective = ''; observations = []; appliedPageSteps.clear(); stopRequested = false
      publish({ messages: [], busy: false, error: '', form: null, saved: false, review: false, progress: '', steps: 0 })
      return true
    }
    const waitEditor = async (token: number) => {
      for (let n = 0; n < 100 && active(token); n++) {
        if (editor()) return editor()
        await pause()
      }
      throw new Error('control.editorUnavailable')
    }
    const run = async (token: number) => {
      genericToken = token
      publish({ busy: true, error: '', saved: false })
      try {
        for (; state.steps < 24 && active(token) && !stopRequested;) {
          if (mutationApproval?.hasPending()) await mutationApproval.drain()
          if (!active(token)) return
          const requestRoute = deps.getRoute(), target = editor(), before = target?.snapshot(), registryBefore = registry?.snapshot(), listBefore = invoiceList?.snapshot(), pageBefore = pageFlow?.snapshot()
          if (target && target.liveVersion !== 1) throw new Error('control.reloadRequired')
          publish({ progress: 'live.reading' })
          const requestController = typeof AbortController === 'undefined' ? null : new AbortController()
          activeRequest = requestController
          let streamedText = ''
          let reply: any
          try {
            reply = await deps.ask(objective, {
              businessId: deps.businessId, year: deps.year, locale: deps.locale(), source: 'plumme-invoice-control',
              history: state.messages.slice(-8),
              signal: requestController?.signal,
              onDelta: (delta: string) => {
                if (!active(token)) return
                if (!delta) { streamedText = ''; clearStreamingMessage(); return }
                streamedText += delta
                streamMessage(streamedText)
              },
              workflowContext: { kind: registry || invoiceList || pageFlow ? 'app_control' : 'invoice_control', bridgeVersion: 1, stage: requestRoute, fiscalYear: deps.year,
                ui: { appSurface: appSurface?.snapshot() || null, editor: before?.ui || null, registry: registryBefore?.ui || null, invoiceList: listBefore?.ui || null, page: pageBefore?.ui || null, vatOptions: deps.vatOptions(), observations: observations.slice(-8), step: state.steps } },
            })
          } finally {
            if (activeRequest === requestController) activeRequest = null
          }
          if (!active(token)) return
          if (deps.getRoute() !== requestRoute || editor() !== target || (before && (editor()?.snapshot().key !== before.key || editor()?.snapshot().ui.layout !== before.ui.layout))) throw new Error('control.editorChanged')
          if (registry && registry.snapshot().key !== registryBefore.key) throw new Error('registry.changed')
          if (invoiceList && invoiceList.snapshot().key !== listBefore.key) throw new Error('listControl.changed')
          if (reply.metadata?.sourceType !== 'agent') throw new Error('control.agentUnavailable')
          const hasUiStep = Boolean(reply.metadata.uiStep || reply.metadata.registryStep || reply.metadata.invoiceListStep || reply.metadata.pageStep || reply.metadata.appStep)
          if (!hasUiStep) {
            const plainAnswer = [reply.answer, ...(reply.lines || [])].filter(Boolean).join('\n\n')
            if (reply.metadata.intentName !== 'assistant_agent' || reply.metadata.executionPayload || reply.metadata.workspace || !plainAnswer) throw new Error('control.reloadRequired')
            settleStreamingMessage(plainAnswer)
            return
          }
          clearStreamingMessage()
          const approval = globalThis.PlummeActionApproval.describe(reply.metadata, { businessId: deps.businessId, editorKey: before?.key, app: appSurface?.snapshot(), editor: before?.ui, registry: registryBefore?.ui, list: listBefore?.ui, page: pageBefore?.ui })
          if (approval) {
            if (approval.presentation === 'invoice_review') target?.review()
            const accepted = await new Promise<boolean>(resolve => {
              approvalResolve = resolve
              publish({ approval: { ...approval, route: requestRoute, id: crypto.randomUUID() }, progress: 'live.approval' })
            })
            if (!accepted || !active(token)) return
            await pause() // Let the approval overlay unmount before validating the page again.
            if (!active(token)) return
            if (deps.getRoute() !== requestRoute || editor() !== target || (before && editor()?.snapshot().key !== before.key)) throw new Error('control.editorChanged')
            if (registry && registry.snapshot().key !== registryBefore.key) throw new Error('registry.changed')
            if (invoiceList && invoiceList.snapshot().key !== listBefore.key) throw new Error('listControl.changed')
            if (pageFlow && pageFlow.snapshot().key !== pageBefore.key) throw new Error('control.editorChanged')
          }
          if (reply.metadata.appStep) {
            genericExecution = true; genericToken = token
            const step = reply.metadata.appStep
            if (!appSurface || step.version !== 1 || reply.metadata.uiStep || reply.metadata.registryStep || reply.metadata.invoiceListStep || reply.metadata.pageStep) throw new Error('control.reloadRequired')
            publish({ steps: state.steps + 1, progress: 'pageControl.working' })
            if (['finish', 'ask_user'].includes(step.action)) { message(step.message || deps.translate('live.finished')); return }
            saving = step.action !== 'inspect'
            try {
              const receipt = await appSurface.perform(step, objective)
              await mutationApproval?.drain()
              if (!active(token)) return
              observations.push(receipt)
            } finally { saving = false }
            await pause()
            continue
          }
          if (reply.metadata.invoiceListStep) {
            const step = reply.metadata.invoiceListStep
            if (!invoiceList || step.version !== 1 || reply.metadata.uiStep || reply.metadata.registryStep) throw new Error('control.reloadRequired')
            publish({ steps: state.steps + 1, progress: 'listControl.working' })
            if (['finish', 'ask_user'].includes(step.action)) { message(step.message || deps.translate('live.finished')); return }
            saving = step.action === 'execute'
            try {
              const receipt = await invoiceList.perform(step, objective, () => stopRequested || !active(token))
              if (!active(token)) return
              observations.push(receipt)
              if (receipt.receipts) {
                for (const item of receipt.receipts) message(`${item.number}: ${deps.translate(item.confirmed ? `listControl.done.${item.result}` : 'listControl.uncertain')}`)
                if (receipt.receipts.length !== receipt.requested) message(deps.translate('listControl.stopped'))
                if (stopRequested || receipt.receipts.length !== receipt.requested || receipt.receipts.some((item: any) => !item.confirmed)) return
                await pause()
                continue
              }
              message(deps.translate(`listControl.done.${receipt.result}`))
            } finally { saving = false }
            await pause()
            continue
          }
          if (reply.metadata.registryStep) {
            const step = reply.metadata.registryStep
            if (!registry || step.version !== 1 || reply.metadata.uiStep) throw new Error('control.reloadRequired')
            publish({ steps: state.steps + 1, progress: 'registry.working' })
            if (['finish', 'ask_user'].includes(step.action)) { message(step.message || deps.translate('registry.review')); return }
            const repeatedRead = ['search', 'lookup_brreg'].includes(step.action) && observations.some((item: any) =>
              item?.action === step.action && item?.kind === step.kind && String(item?.query || '').trim().toLowerCase() === String(step.query || '').trim().toLowerCase())
            if (repeatedRead) { message(deps.translate('registry.repeatedRead')); return }
            if (step.action === 'save_record') {
              const prepared = registry.prepareVisibleSave(step, objective)
              publish({ form: { id: crypto.randomUUID(), recordId: prepared.draft.id, kind: step.kind, initial: prepared.draft,
                isNew: prepared.isNew, editorKey: null, standalone: true, autoSave: true }, progress: 'registry.working' })
              message(deps.translate('registry.working'))
              return
            }
            saving = registry.isMutation(step)
            try {
              const receipt = await registry.perform(step, objective)
              if (!active(token)) return
              observations.push(receipt)
              message(deps.translate(`registry.done.${receipt.result}`))
            } finally { saving = false }
            await pause()
            if (!active(token)) return
            if (deps.getRoute() !== registry.routes[step.kind]) throw new Error('control.editorChanged')
            continue
          }
          if (reply.metadata.pageStep) {
            const step = reply.metadata.pageStep
            if (!pageFlow || step.version !== 1 || reply.metadata.uiStep || reply.metadata.registryStep || reply.metadata.invoiceListStep) throw new Error('control.reloadRequired')
            const fingerprint = JSON.stringify({ action: step.action, page: step.page, operation: step.operation || '', fields: step.fields || {} })
            if (appliedPageSteps.has(fingerprint)) throw new Error(deps.translate('pageControl.noProgress'))
            publish({ steps: state.steps + 1, progress: 'pageControl.working' })
            if (['finish', 'ask_user'].includes(step.action)) { message(step.message || deps.translate('pageControl.unverified')); return }
            saving = step.action === 'submit'
            try {
              const receipt = await pageFlow.perform(step, objective)
              if (!active(token)) return
              appliedPageSteps.add(fingerprint)
              observations.push(receipt)
              if (receipt.confirmed !== true) throw new Error(deps.translate('pageControl.unverified'))
              if (step.action === 'submit') message(deps.translate('pageControl.done.saved'))
            } finally { saving = false }
            await pause()
            if (step.action === 'submit' || step.action === 'open_review') return
            continue
          }
          const step = reply.metadata.uiStep
          if (!step || step.version !== 1) throw new Error('control.reloadRequired')
          publish({ steps: state.steps + 1, progress: `live.${step.action}` })
          if (['finish', 'ask_user'].includes(step.action)) {
            message(step.message || deps.translate(observations.some(item => item.page) ? 'pageControl.unverified' : step.action === 'finish' && observations.length ? 'live.finished' : 'live.clarification')); return
          }
          if (['create_customer', 'create_product', 'issue_invoice', 'send_invoice', 'save_draft'].includes(step.action)) {
            const quote = String(step.authorizationQuote || '').trim()
            if (quote.length < 4 || !objective.includes(quote) || (step.recipientEmail && !objective.includes(step.recipientEmail))) throw new Error('live.authorizationRequired')
            if (!target) throw new Error('control.editorUnavailable')
            saving = true
            try {
              if (step.action === 'create_customer' || step.action === 'create_product') {
                saving = false
                const kind = step.action === 'create_customer' ? 'customer' : 'product'
                publish({ form: { id: crypto.randomUUID(), kind, initial: step[kind], isNew: true,
                  editorKey: target.snapshot().key, autoSave: true }, progress: 'registry.working' })
                message(deps.translate('registry.working'))
                return
              }
              if (step.action === 'save_draft') { await target.save(); message(deps.translate('control.saved')); return }
              const result = await target.fulfill(step)
              if (!active(token)) return
              message(deps.translate(before?.ui?.documentType && before.ui.documentType !== 'faktura' ? (result.emailSent ? 'listControl.done.sent' : 'listControl.done.issued') : (result.emailSent ? 'live.invoiceSent' : 'live.invoiceIssued')))
              return
            } finally { saving = false }
          }
          if (step.action === 'open_editor') {
            if (registry?.getSnapshot().dirty) throw new Error('registry.unsaved')
            registry?.close()
            if (!target) deps.navigate('faktura')
            const opened = await waitEditor(token)
            if (!active(token)) return
            if (opened.liveVersion !== 1) throw new Error('control.reloadRequired')
            opened.setLayout('onepage')
          } else if (['open_customer_form', 'open_product_form'].includes(step.action)) {
            if (!target) throw new Error('control.editorUnavailable')
            const kind = step.action === 'open_customer_form' ? 'customer' : 'product'
            const initial = step[kind]
            if (!initial || typeof initial.name !== 'string' || !initial.name.trim()) throw new Error('control.incomplete')
            if (kind === 'product' && !deps.vatOptions().some((v: any) => String(v.value) === initial.vatSelection)) throw new Error('control.advancedInvoice')
            publish({ form: { id: crypto.randomUUID(), kind, initial, isNew: true, editorKey: target.snapshot().key }, progress: 'live.approval' })
            message(deps.translate(kind === 'customer' ? 'live.customerApproval' : 'live.productApproval')); return
          } else if (step.action === 'request_save') {
            message(deps.translate('control.saveReady')); return
          } else if (step.action === 'review') {
            if (!target) throw new Error('control.editorUnavailable')
            target.review(); message(deps.translate('control.reviewHelp')); return
          } else if (step.action === 'set_layout') {
            if (!target || !['steps', 'onepage'].includes(step.layout)) throw new Error('control.invoiceOnly')
            target.setLayout(step.layout)
          } else {
            if (!target) throw new Error('control.editorUnavailable')
            const verify = target.perform(step)
            await pause()
            if (!active(token)) return
            if (editor() !== target || !verify()) throw new Error('live.notApplied')
          }
          await pause()
          if (!active(token)) return
          const observed = editor()?.snapshot()
          if (!observed) throw new Error('control.editorUnavailable')
          if (step.action === 'set_layout' && observed.ui.layout !== step.layout) throw new Error('live.notApplied')
          observations.push({ action: step.action, field: step.field, lineIndex: step.lineIndex, result: 'applied' })
          deps.highlight?.(step)
          message(deps.translate(`live.done.${step.action}`))
        }
        if (active(token) && !stopRequested) throw new Error('live.limit')
      } catch (error) {
        if (active(token)) { clearStreamingMessage(); publish({ error: error instanceof Error ? error.message : 'control.failed' }) }
      } finally {
        if (active(token)) publish({ busy: saving, progress: state.form ? (state.form.autoSave ? 'registry.working' : 'live.approval') : '' })
      }
    }
    const ask = async (question: string) => {
      if (state.busy || state.approval || saving || disposed || !deps.isCurrent() || !question.trim()) return
      objective = question; observations = []; appliedPageSteps = new Set<string>(); stopRequested = false
      invoiceList?.beginRequest()
      pageFlow?.beginRequest()
      appSurface?.beginRequest()
      publish({ form: null, steps: 0, messages: [...state.messages, { role: 'user', content: question }].slice(-30) })
      await run(++generation)
    }
    const canUseForm = (id: string) => !disposed && deps.isCurrent() && state.form?.id === id
    const beginFormSave = (id: string) => {
      if (saving || !canUseForm(id)) return false
      if (!state.form.standalone && editor()?.snapshot().key !== state.form.editorKey) { publish({ error: 'control.editorChanged' }); return false }
      saving = true; publish({ busy: true }); return true
    }
    const formSaveFailed = (id: string, error: string) => {
      saving = false
      if (canUseForm(id)) publish({ busy: false, error })
    }
    const formSaved = async (id: string, record: any) => {
      saving = false
      if (!canUseForm(id) || !record?.id) return
      const form = state.form
      observations.push({ action: `${form.isNew ? 'create' : 'update'}_${form.kind}`, result: 'saved_by_user', id: record.id, name: record.name })
      publish({ form: null, busy: false, error: '' })
      message(deps.translate(form.standalone ? 'registry.done.saved' : form.kind === 'customer' ? 'live.customerSaved' : 'live.productSaved'))
      if (form.standalone) { registry?.close(); await run(++generation); return }
      if (editor()?.snapshot().key !== form.editorKey) { publish({ error: 'control.editorChanged' }); return }
      // Re-read current editor and registry state after the user's save.
      await run(++generation)
    }
    const cancelForm = (id: string) => {
      if (saving || !canUseForm(id)) return
      const kind = state.form.kind, standalone = state.form.standalone
      observations.push({ action: `create_${kind}`, result: 'cancelled_by_user' })
      stop(); if (standalone) registry?.close(); message(deps.translate('live.cancelled'))
    }
    const save = async () => {
      if (state.busy || saving || state.form || disposed || !deps.isCurrent()) return
      const target = editor()
      if (!target) { publish({ error: 'control.editorUnavailable' }); return }
      saving = true; const token = ++generation; publish({ busy: true, error: '' })
      try { await target.save(); if (active(token)) publish({ saved: true }) }
      catch (error) { if (active(token)) publish({ error: error instanceof Error ? error.message : 'control.failed' }) }
      finally { saving = false; if (active(token)) publish({ busy: false }) }
    }
    const editRegistry = (key: string, value: any) => {
      if (saving || disposed) return
      stop()
      try { registry.edit(key, value) } catch (error: any) { publish({ error: error.message }) }
    }
    const closeRegistry = () => { if (!saving) { stop(); registry?.close() } }
    const saveRegistry = async () => {
      if (!registry || state.busy || saving || disposed || !deps.isCurrent()) return
      const selected = registry.getSnapshot()
      saving = true; const token = ++generation; publish({ busy: true, error: '', progress: 'registry.working' })
      try {
        const receipt = await registry.perform({ action: 'save_record', kind: selected.kind }, '', true)
        if (active(token)) message(deps.translate(`registry.done.${receipt.result}`))
      } catch (error: any) { if (active(token)) publish({ error: error.message }) }
      finally { saving = false; if (active(token)) publish({ busy: false, progress: '' }) }
    }
    return { ask, stop, reset, decideApproval, save, editRegistry, closeRegistry, saveRegistry, beginFormSave, formSaveFailed, formSaved, cancelForm, canUseForm,
      getSnapshot: () => state,
      subscribe: (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn) } },
      dispose: () => { cancelApproval(); activeRequest?.abort(); activeRequest = null; disposed = true; generation++; unsubscribeRegistry?.(); unsubscribeList?.(); unsubscribePageFlow?.(); pageFlow?.dispose?.(); appSurface?.dispose?.(); listeners.clear() },
    }
  }
  return { createSession }
})()
Object.assign(globalThis, { PlummeInvoiceLiveControl: invoiceLiveControlApi })
